import {
  Esim,
  EsimPlan,
  GetEsimPlansResponse,
  TopUpOptionsResponse,
  Country,
  Region,
  PublicTopUpOptionsResponse,
  SinglePublicPlan,
  EsimAutoTopUpHistoryResponse,
  ComparisonData,
  PublicAllPlansResponse
} from "@/types/esim";
import { throwForFailedResponse } from "@/lib/errors/error-handler";
async function getJson<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options?.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));

  if (!res.ok || data?.success === false) {
    throwForFailedResponse(res, data, "Request failed");
  }
  return (data?.data ?? data) as T;
}

export const esimService = {
  async getEsim(): Promise<Esim[]> {
    return getJson<Esim[]>("/api/esim");
  },
  async getCountries(): Promise<Country[]> {
    return getJson<Country[]>("/api/esim/countries");
  },
  async getRegion(): Promise<Region[]> {
    return getJson<Region[]>("/api/esim/regions");
  },
  async getEsimByIccid(iccid: string): Promise<EsimPlan[]> {
    const res = await getJson<GetEsimPlansResponse>(
      `/api/esim/plans?iccid=${encodeURIComponent(iccid)}`,
    );
    return res?.data?.plans ?? [];
  },
  async getTopUpOptions(
    keyword: string,
    page: number,
    isCountry: boolean,
    per_page: number = 10,
    filters?: {
      dataGb?: string;
      validity?: string;
      planType?: string;
    },
  ): Promise<TopUpOptionsResponse> {
    const params = new URLSearchParams();

    params.append("keyword", keyword);
    params.append("page", page.toString());
    params.append("per_page", per_page.toString());
    params.append("isCountry", isCountry.toString());

    if (filters?.dataGb && filters.dataGb !== "all") {
      params.append("data_quota_gb", filters.dataGb);
    }

    if (filters?.validity && filters.validity !== "all") {
      params.append("validity_days", filters.validity);
    }

    if (filters?.planType && filters.planType !== "all") {
      params.append("data_plan_type", filters.planType);
    }

    const query = params.toString();

    return getJson<TopUpOptionsResponse>(`/api/esim/top-up${query ? `?${query}` : ""}`);
  },
  async topUpEsim(payload: { planSlug: string; iccid: string; loyalty_points?: number }) {
    const res = await fetch("/api/esim/top-up", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) {
      throwForFailedResponse(res, data, "Failed to create");
    }
    return data?.data ?? data;
  },
  async createEsim(payload: { plan_id: number; loyalty_points?: number }) {
    const res = await fetch("/api/esim", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) {
      throwForFailedResponse(res, data, "Failed to create");
    }
    return data?.data ?? data;
  },
  async createEsimWithCreditCard(payload: { plan_id: number; loyalty_points?: number }) {
    const res = await fetch("/api/esim/credit-card", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) {
      throwForFailedResponse(res, data, "Failed to create");
    }
    return data?.data ?? data;
  },
  async getPlanBySlug(slug: string): Promise<SinglePublicPlan> {
    return getJson<SinglePublicPlan>(`/api/esim/plans/slug?slug=${encodeURIComponent(slug)}`);
  },

  async esimTopUpWithCreditCard(payload: { iccid: string; planSlug: string; loyalty_points?: number }) {
    const res = await fetch("/api/esim/credit-card/top-up", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) {
      throwForFailedResponse(res, data, "Failed to top up via card");
    }
    return data?.data ?? data;
  },

  async autoTopUpEsim(payload: { id: string, auto_topup_enabled: number }){
    const res = await fetch(
      '/api/esim/auto-top-up',
      {
        method : 'PUT',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) {
      throwForFailedResponse(res, data, "Failed to handle auto top-up");
    }
    return data?.data ?? data;
  },

  async getAutoTopUpHistory(
    id: string,
  ): Promise<EsimAutoTopUpHistoryResponse> {
    const params = new URLSearchParams();
    params.append("plan_id", id);
    const query = params.toString();

    return getJson<EsimAutoTopUpHistoryResponse>(`/api/esim/auto-top-up${query ? `?${query}` : ""}`);
  },

  async getPromoCodes() {
    return getJson("/api/esim/promo-codes");
  },

  async applyPromoCode(payload: { slug: string; promo_code: string }) {
    const res = await fetch("/api/esim/promo-codes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) {
      throwForFailedResponse(res, data, "Failed to apply promo code");
    }
    return data?.data ?? data;
  },  

  // Public APIs
  async getPopularEsimPlans() {
    const res = await fetch("/api/public/esim/popular");
    if (!res.ok) throw new Error("Failed to load popular plans");
    return res.json();
  },
  async getPublicCountries(): Promise<Country[]> {
    return getJson<Country[]>("/api/public/esim/countries");
  },
  async getPublicRegions(): Promise<{ status: string; message: string; data: Region[] }> {
    return getJson<{ status: string; message: string; data: Region[] }>(
      "/api/public/esim/available-regions",
    );
  },
  async getPublicPlansByRegion(
    region_code: string,
    page: number,
    per_page: number = 10,
    filters?: {
      dataGb?: string;
      validity?: string;
      planType?: string;
    },
  ): Promise<PublicTopUpOptionsResponse> {
    const params = new URLSearchParams();
    params.append("region_code", region_code);
    params.append("page", page.toString());
    params.append("per_page", per_page.toString());

    if (filters?.dataGb && filters.dataGb !== "all") {
      params.append("data_quota_gb", filters.dataGb);
    }

    if (filters?.validity && filters.validity !== "all") {
      params.append("validity_days", filters.validity);
    }

    if (filters?.planType && filters.planType !== "all") {
      params.append("data_plan_type", filters.planType);
    }

    const query = params.toString();
    return getJson<PublicTopUpOptionsResponse>(
      `/api/public/esim/region-plans/${query ? `?${query}` : ""}`,
    );
  },
  async getPublicPlansByCountry(
    keyword: string,
    page: number,
    per_page: number = 10,
    filters?: {
      dataGb?: string;
      validity?: string;
      planType?: string;
    },
  ): Promise<PublicTopUpOptionsResponse> {
    const params = new URLSearchParams();
    params.append("keyword", keyword);
    params.append("page", page.toString());
    params.append("per_page", per_page.toString());

    if (filters?.dataGb && filters.dataGb !== "all") {
      params.append("data_quota_gb", filters.dataGb);
    }

    if (filters?.validity && filters.validity !== "all") {
      params.append("validity_days", filters.validity);
    }

    if (filters?.planType && filters.planType !== "all") {
      params.append("data_plan_type", filters.planType);
    }

    const query = params.toString();
    return getJson<PublicTopUpOptionsResponse>(
      `/api/public/esim/country-plans/${query ? `?${query}` : ""}`,
    );
  },
  async getPublicPlanBySlug(slug: string): Promise<SinglePublicPlan> {
    return getJson<SinglePublicPlan>(`/api/public/esim/slug?slug=${encodeURIComponent(slug)}`);
  },
  async createPublicEsim(payload: { plan_id: string; email: string }) {
    const res = await fetch("/api/public/esim", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) {
      throwForFailedResponse(res, data, "Failed to create");
    }
    return data?.data ?? data;
  },

  async getCompetitorComparison(
      scope: string, 
      currency?: string, 
      is_home_page?: boolean
    ): Promise<ComparisonData> {
    const params = new URLSearchParams();
    params.append("scope", scope);
    if (currency) {
      params.append("currency", currency);
    }
    if (is_home_page) {
      params.append("home_page", '1');
    }
    return getJson<ComparisonData>(`/api/public/esim/comparison?${params.toString()}`);
  },

  async getAllPublicPlans(
    countryOrRegionName: string,
    page: number = 1,
    per_page: number = 20,
    filters?: {
      dataGb?: string;
      validity?: string;
      planType?: string;
      sort?: "asc" | "desc";
    },
  ): Promise<PublicAllPlansResponse> {
    const params = new URLSearchParams();
    params.append("keyword", countryOrRegionName);
    params.append("page", page.toString());
    params.append("per_page", per_page.toString());
    if (filters?.sort) params.append("sort", filters.sort);
    if (filters?.dataGb && filters.dataGb !== "all") params.append("data_quota_gb", filters.dataGb);
    if (filters?.validity && filters.validity !== "all") params.append("validity_days", filters.validity);
    if (filters?.planType && filters.planType !== "all") params.append("data_plan_type", filters.planType);
    const query = params.toString();
    return getJson<PublicAllPlansResponse>(
      `/api/public/esim/all-plans/${query ? `?${query}` : ""}`,
    );
  },
  async checkDeviceCompatibility(device: string) {
    return getJson(
      `/api/public/esim/device-compatibility?device=${encodeURIComponent(device)}`,
    );
  },
  async getAllDevices(page = 1, perPage = 50) {
    return getJson<{
      items: { name: string }[];
      meta: { current_page: number; per_page: number; total: number; last_page: number };
    }>(`/api/public/esim/all-devices?page=${page}&per_page=${perPage}`);
  },
};

